import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { findConsultation, CONSULTATIONS } from "../data/consultations";
import MagneticButton from "../components/MagneticButton";

const ConsultationDetail = () => {
  const { slug } = useParams();
  const c = findConsultation(slug);
  if (!c) return <Navigate to="/services" replace />;

  const next = CONSULTATIONS[(CONSULTATIONS.findIndex((x) => x.slug === slug) + 1) % CONSULTATIONS.length];

  return (
    <div data-testid={`consultation-detail-${slug}`} className="pt-32 md:pt-40 pb-24">
      <div className="px-6 md:px-12 lg:px-20">
        <Link
          to="/services"
          data-testid="back-to-consultations"
          className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-muted-ink hover:text-[#0a0a0b] transition-colors"
        >
          <ArrowLeft size={14} /> All Services
        </Link>

        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">
            {c.n} · {c.category}
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="consultation-detail-title"
          className="mt-6 font-display font-medium tracking-tight leading-[0.95] text-[#0a0a0b] max-w-5xl"
          style={{ fontSize: "clamp(2.4rem, 7vw, 6.5rem)" }}
        >
          {c.title}
        </motion.h1>
        <p className="mt-6 accent-text font-medium text-lg md:text-xl">{c.tagline}</p>
        <p className="mt-8 max-w-3xl text-base md:text-lg text-muted-ink leading-relaxed">{c.body}</p>
      </div>

      <div className="mt-20 md:mt-24 px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 bg-surface hairline rounded-[2px] p-8 md:p-10">
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">What&apos;s inside</span>
          <h2 className="mt-3 font-display font-medium text-[#0a0a0b] text-2xl md:text-3xl">Capabilities</h2>
          <ul className="mt-8 space-y-4">
            {c.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="mt-1.5 inline-block w-2 h-2 rounded-full accent-gradient shrink-0" />
                <span className="text-[#0a0a0b]">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5 bg-surface hairline rounded-[2px] p-8 md:p-10">
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Deliverables</span>
          <h2 className="mt-3 font-display font-medium text-[#0a0a0b] text-2xl md:text-3xl">What you get</h2>
          <ul className="mt-8 space-y-3">
            {c.deliverables.map((d) => (
              <li key={d} className="flex items-start gap-3 text-[#0a0a0b]">
                <Check size={16} className="mt-1 shrink-0" /> {d}
              </li>
            ))}
          </ul>
          <div className="mt-10 pt-6 border-t border-[var(--hairline)]">
            <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Who it&apos;s for</span>
            <p className="mt-3 text-sm md:text-base text-[#0a0a0b]">{c.who}</p>
          </div>
        </div>
      </div>

      <div className="mt-20 md:mt-28 px-6 md:px-12 lg:px-20 flex items-end justify-between flex-wrap gap-8">
        <div>
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Ready to delve?</span>
          <h3
            className="mt-3 font-display font-medium tracking-tight text-[#0a0a0b]"
            style={{ fontSize: "clamp(1.8rem, 3.6vw, 3rem)" }}
          >
            Let&apos;s design your <span className="accent-text">next move.</span>
          </h3>
          <div className="mt-8">
            <MagneticButton as="a" href="/contact" testId="consultation-cta-contact">
              Start a project <ArrowUpRight size={16} strokeWidth={1.5} />
            </MagneticButton>
          </div>
        </div>

        <Link
          to={`/services/${next.slug}`}
          data-testid="next-consultation"
          className="group max-w-sm block"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Next service</span>
          <p className="mt-3 font-display text-2xl md:text-3xl text-[#0a0a0b] group-hover:accent-text transition-colors">
            {next.title} <ArrowUpRight size={20} className="inline" />
          </p>
        </Link>
      </div>
    </div>
  );
};

export default ConsultationDetail;
